import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface Props {
  open: boolean;
  onClose: () => void;
  orgId?: string;
  projectId?: string;
  sessionId?: string;
}

/**
 * Create / copy / revoke a public share link for a session replay. Anyone with
 * the link can watch the replay without signing in, until it's revoked.
 */
export function ShareDialog({ open, onClose, orgId, projectId, sessionId }: Props) {
  const all = useT();
  const t = all.replay.share;
  const common = all.common;
  const qc = useQueryClient();
  const enabled = open && !!orgId && !!projectId && !!sessionId;
  const base = `/v1/orgs/${orgId}/projects/${projectId}/sessions/${sessionId}/share`;
  const key = ['session-share', sessionId];
  const [copied, setCopied] = useState(false);

  const share = useQuery({
    enabled,
    queryKey: key,
    queryFn: async () => (await api.get(base)).data as { token: string | null },
  });
  const create = useMutation({
    mutationFn: async () => (await api.post(base)).data as { token: string },
    onSuccess: (d) => qc.setQueryData(key, d),
  });
  const revoke = useMutation({
    mutationFn: async () => api.delete(base),
    onSuccess: () => qc.setQueryData(key, { token: null }),
  });

  const token = create.data?.token ?? share.data?.token ?? null;
  const url = token ? `${window.location.origin}/share/${token}` : '';

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the field is selectable as a fallback */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShareIcon fontSize="small" color="primary" />
        {t.title}
      </DialogTitle>
      <DialogContent>
        {share.isLoading ? (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">{common.loading}</Typography>
          </Stack>
        ) : token ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t.body}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={url}
              InputProps={{
                readOnly: true,
                onFocus: (e) => e.target.select(),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={copy} color={copied ? 'success' : 'default'}>
                      {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {revoke.isError && <Alert severity="error" sx={{ mt: 1 }}>{t.revokeFailed}</Alert>}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            This replay is private. Create a public link to let anyone watch it without signing in.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {token ? (
          <Button color="error" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
            {revoke.isPending ? t.revoking : t.revoke}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={create.isPending ? <CircularProgress size={14} color="inherit" /> : <ShareIcon />}
            disabled={create.isPending || !enabled}
            onClick={() => create.mutate()}
          >
            {create.isPending ? t.creating : t.create}
          </Button>
        )}
        <Button onClick={onClose}>{common.close}</Button>
      </DialogActions>
    </Dialog>
  );
}
